import { type DeepPartial } from '@apollo/client/utilities';
import { type ResultOf, type TypedDocumentNode } from '@graphql-typed-document-node/core';

import { type FragmentType } from './_generated/fragment-masking';

/**
 * Represents a masked fragment returned by a query.
 *
 * @example
 * interface CardProps {
 *   data: Masked<typeof Card_CardFragment>;
 * }
 *
 * function Card({ data }: CardProps) {
 *   const { ... } = unmask<typeof Card_CardFragment>(data);
 * }
 *
 * @remarks
 * - This is most commonly used as the `data` prop on a component.
 * - The data can be expanded using {@link unmask}.
 */
export type Masked<T extends TypedDocumentNode<unknown, unknown>> = FragmentType<T>;

export function unmask<T extends TypedDocumentNode<unknown, unknown>>(
  fragment: Masked<T>,
): ResultOf<T>;
export function unmask<T extends TypedDocumentNode<unknown, unknown>>(
  fragment: Masked<T> | undefined,
): ResultOf<T> | undefined;
export function unmask<T extends TypedDocumentNode<unknown, unknown>>(
  fragment: Array<Masked<T>>,
): Array<ResultOf<T>>;
export function unmask<T extends TypedDocumentNode<unknown, unknown>>(
  fragment: Array<Masked<T> | undefined>,
): Array<ResultOf<T> | undefined>;
/**
 * Unmasks a {@link Masked masked fragment} into its original type. Note that this function
 * is a noop and only changes the type of the fragment.
 *
 * @example Expanding a union type
 * type CardFragment = typeof Card_AnnualReportFragment | typeof Card_ArticleFragment;
 *
 * interface CardProps {
 *   data: Masked<CardFragment>;
 * }
 *
 * function Card({ data }: CardProps) {
 *   const { ... } = unmask<CardFragment>(data);
 * }
 */
export function unmask<T extends TypedDocumentNode<unknown, unknown>>(
  fragment: Masked<T> | undefined | Array<Masked<T> | undefined>,
): ResultOf<T> | undefined | Array<ResultOf<T> | undefined> {
  return fragment as never;
}

/**
 * Creates a {@link Masked masked fragment} from an object. This is usually used in testing.
 *
 * TODO: Lots of things depend on __typename. Should we create a separate test utility to construct
 *  mocks in a more thoughtful way?
 *
 * TODO: Fragments spread on the root fragment cause type errors. We should add a second argument
 *  here that allows us to pass a few `Masked` fragments.
 */
export function mask<T extends TypedDocumentNode<unknown, unknown>>(
  data: DeepPartial<ResultOf<T>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Masked<T> & { __typename: any } {
  return data as Masked<T>;
}
